import { Payouts } from '@/components/Payouts/admin/Payouts';
import { installNumberInputWheelGuard } from '@/lib/numberInputWheelGuard';
import { ensureAppOriginCookie } from '@/lib/trackingCookie';
import { ResponsiveProvider, ThemeProvider } from '@palmetto/palmetto-components';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';

import { ItcEligibilityProgramTypes, UserPermissions } from 'types';

import AuthenticatingWrapper from './AuthenticatingWrapper';
import { Account, Accounts } from './components';
import { CreateAccount } from './components/Account/CreateAccount';
import { EditAccount } from './components/Account/EditAccount';
import { ActivationMilestonePackage } from './components/ActivationMilestonePackage/ActivationMilestonePackage';
import { ContractTemplates } from './components/AdminContracts/ContractTemplates';
import { AdminTasks } from './components/AdminTasks/AdminTasks';
import { ITCCalculator } from './components/AdminTasks/ITCCalculator';
import { AgingDashboard } from './components/AgingDashboard/AgingDashboard';
import { AuthProvider } from './components/auth/authProvider';
import RequirePalmettoFinanceUser from './components/auth/RequirePalmettoFinanceUser';
import { RequirePalmettoUserHasProgramTypes } from './components/auth/RequirePalmettoUserHasProgramTypes';
import RequirePermissions from './components/auth/requirePermissions';
import { BillingBlockAdmin } from './components/BillingBlocks/BillingBlockAdmin';
import { BulkPaymentLinkAdmin } from './components/BulkPaymentLinks/BulkPaymentLinkAdmin';
import { ChangeOrderOffer } from './components/ChangeOrder/ChangeOrderOffer';
import { CreateChangeOrder } from './components/ChangeOrder/CreateChangeOrder';
import { ContractSigning } from './components/ContractSigning/ContractSigning';
import { ApplicationForm } from './components/CreditCheck/ApplicationForm';
import { ApplicationSuccess } from './components/CreditCheck/ApplicationSuccess';
import { EditProfile } from './components/CurrentUser/EditProfile';
import { YourProfile } from './components/CurrentUser/YourProfile';
import { CustomerDirectPayment } from './components/CustomerDirectPayment/CustomerDirectPayment';
import { LoopFieldDesignDocSubmission } from './components/DesignDocuments/LoopFieldDesignDocSubmission';
import { DisclosureConfirmation } from './components/Disclosures/DisclosureConfirmation';
import { DisclosureForm } from './components/Disclosures/DisclosureForm';
import { DocumentPull } from './components/DocumentPull';
import { FlagGuard } from './components/FlagGuard/FlagGuard';
import { AssignAccounts } from './components/Funds/AssignAccounts';
import { Fund } from './components/Funds/Fund';
import { FundHistory } from './components/Funds/FundHistory';
import { FundOverview } from './components/Funds/FundOverview';
import { Funds } from './components/Funds/Funds';
import { UnAssignAccounts } from './components/Funds/UnAssignAccounts';
import { Incentives } from './components/Incentives';
import { AllocateAccounts } from './components/Incentives/AllocateAccounts';
import { BlockAccounts } from './components/Incentives/BlockAccounts';
import { IncentiveDetails } from './components/Incentives/IncentiveDetails';
import { UnallocateAccounts } from './components/Incentives/UnallocateAccounts';
import { InstallMilestonePackage } from './components/InstallMilestonePackage/InstallMilestonePackage';
import { VerifyExistingSystem } from './components/InstallMilestonePackage/VerifyExistingSystem';
import { IntercomDisabledRoute } from './components/IntercomDisabledRoute';
import { ITCSystemDesign } from './components/ITCSystemDesign/ITCSystemDesign';
import { LeadManagement } from './components/LeadManagement/LeadManagement';
import { LoopFieldMilestonePackageFactory } from './components/LoopFieldMilestonePackage/LoopFieldMilestonePackageFactory';
import { Markets } from './components/Markets/Markets';
import { PastDueAccounts } from './components/PastDueAccounts/PastDueAccounts';
import { AdvancePtoForm } from './components/PermissionToOperate/AdvancePtoForm';
import { PostActivationMilestonePackage } from './components/PostActivationMilestonePackage/PostActivationMilestonePackage';
import { PreBillingAdjustmentOffer } from './components/PreBillingAdjustment/PreBillingAdjustmentOffer';
import { BulkEditMappings } from './components/Pricing/Mappings/BulkEdit/BulkEditMappings';
import { BulkJobs } from './components/Pricing/Mappings/BulkJobs/BulkJobs';
import { CreateMapping } from './components/Pricing/Mappings/CreateMapping';
import { EditMapping } from './components/Pricing/Mappings/EditMapping';
import { CreateCapSchedule } from './components/Pricing/PriceCaps/CreateCapSchedule';
import EditPriceSheet from './components/Pricing/PriceSheetDetails/EditPriceSheet';
import PriceSheet from './components/Pricing/PriceSheetDetails/PriceSheet';
import { PriceSheetDetails } from './components/Pricing/PriceSheetDetails/PriceSheetDetails';
import { PriceSheetMappings } from './components/Pricing/PriceSheetDetails/PriceSheetMappings';
import { Pricing } from './components/Pricing/Pricing';
import { ProductionValueCheck } from './components/ProductionValueCheck/ProductionValueCheck';
import { FlagDetailsPage } from './components/QualityControl/FlagDetailsPage';
import { QualityControlSettings } from './components/QualityControl/QualityControlSettings';
import { ProgramEnablement } from './components/Queues/HoldMotion/ProgramEnablement';
import { Queue } from './components/Queues/Queue';
import { CreateQuote } from './components/Quotes/CreateQuote';
import { ReplayPrivacyMask } from './components/ReplayPrivacyMask';
import { SalesRepDetails } from './components/SalesReps/SalesRepDetails';
import { SalesRepHistoryView } from './components/SalesReps/SalesRepHistoryView';
import { SalesReps, SalesRepsListView } from './components/SalesReps/SalesReps';
import { SalesRepAuthentication } from './components/SalesRepVerification/SalesRepAuthentication';
import { VerificationError } from './components/SalesRepVerification/VerificationError';
import { VerificationSuccess } from './components/SalesRepVerification/VerificationSuccess';
import { Settings } from './components/Settings';
import { Queues } from './components/TaskQueues/Queues';
import getConfigValue from './config';
import { DsmModeSync } from './dsm-components';
import { ApprovedVendorItemDetail } from './dsm-components/approved-vendor-list/ApprovedVendorItemDetail';
import './index.css';
import { ApprovedVendorItemForm } from './dsm-components/approved-vendor-list/ApprovedVendorItemForm';
import { ApprovedVendorList } from './dsm-components/approved-vendor-list/ApprovedVendorList';
import { ChecklistTemplates } from './dsm-components/checklist-templates';
import { ChecklistAdminLayout } from './dsm-components/checklist-templates/ChecklistAdminLayout';
import { ChecklistTemplateDetail } from './dsm-components/checklist-templates/ChecklistTemplateDetail';
import { ChecklistTemplateForm } from './dsm-components/checklist-templates/ChecklistTemplateForm';
import { EquipmentManufacturerDetail } from './dsm-components/equipment-manufacturers/EquipmentManufacturerDetail';
import { EquipmentManufacturerForm } from './dsm-components/equipment-manufacturers/EquipmentManufacturerForm';
import { EquipmentManufacturerList } from './dsm-components/equipment-manufacturers/EquipmentManufacturerList';
import { HardwareExceptionsTable } from './dsm-components/hardware-exceptions/HardwareExceptionsTable';
import { ProductLineDetail, ProductLineForm, ProductLineList } from './dsm-components/product-lines';
import { PromosTable } from './dsm-components/promos';
import { SystemLossThresholds } from './dsm-components/system-loss-thresholds';
import { WelcomeCallStipRules } from './dsm-components/welcome-call-stip-rules';
import Error from './Error';
import { isNextEnvironment } from './helpers/isNextEnvironment';
import { AccountctlApp } from './pages/internal/accountctl';
import { CongaTestApp } from './pages/internal/congaTest';
import { initDataDog, tagInviteTokenSession } from './services/datadog';
import { store } from './store';

const intercomAppId = getConfigValue('VITE_APP_INTERCOM_ID') || '';

const launchDarklyClientId = getConfigValue('VITE_APP_LAUNCH_DARKLY_CLIENT_ID') || '';
const anonymousUserKey = 'unknown-user-key';
ensureAppOriginCookie();
installNumberInputWheelGuard();
initDataDog();
tagInviteTokenSession();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const router = createBrowserRouter([
  {
    path: 'accounts/:id/pre-billing-adjustment-offer',
    element: (
      <IntercomDisabledRoute>
        <ReplayPrivacyMask>
          <PreBillingAdjustmentOffer />
        </ReplayPrivacyMask>
      </IntercomDisabledRoute>
    ),
  },
  {
    path: 'accounts/:id/direct-payment',
    element: (
      <IntercomDisabledRoute>
        <ReplayPrivacyMask>
          <CustomerDirectPayment />
        </ReplayPrivacyMask>
      </IntercomDisabledRoute>
    ),
  },
  {
    path: 'contract-signing',
    element: (
      <IntercomDisabledRoute>
        <ReplayPrivacyMask>
          <ContractSigning />
        </ReplayPrivacyMask>
      </IntercomDisabledRoute>
    ),
    errorElement: <Error />,
  },
  {
    path: 'accounts/:id/change-order-offer/:changeOrderId',
    element: (
      <IntercomDisabledRoute>
        <ReplayPrivacyMask>
          <ChangeOrderOffer />
        </ReplayPrivacyMask>
      </IntercomDisabledRoute>
    ),
    errorElement: <Error />,
  },
  {
    path: '/',
    element: <AuthenticatingWrapper />,
    errorElement: <Error />,
    children: [
      {
        path: '/admin/funds',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachPaymentAdmin]}>
              <Funds />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/funds/:key',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachPaymentAdmin]}>
              <Fund />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
        children: [
          {
            index: true,
            element: <FundOverview />,
          },
          {
            path: '/admin/funds/:key/history',
            element: <FundHistory />,
          },
        ],
      },
      {
        path: '/admin/funds/:key/assign',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachPaymentAdmin]}>
              <AssignAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/funds/:key/unassign',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachPaymentAdmin]}>
              <UnAssignAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/document-pull/*',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachCapitalMarketsAdmin]}>
              <DocumentPull />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/incentives',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
              <Incentives />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/billing-blocks',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.billingBlockerAdmin]}>
              <BillingBlockAdmin />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/bulk-payment-links',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachSupport]}>
              <BulkPaymentLinkAdmin />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/past-due-accounts',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachSupport]}>
              <PastDueAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/incentives/:key/allocate',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
              <AllocateAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/incentives/:key/block',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
              <BlockAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/incentives/:key/unallocate',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
              <UnallocateAccounts />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/incentives/:key/*',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
              <IncentiveDetails />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/markets',
        element: (
          <RequirePalmettoFinanceUser>
            <Markets />
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/contracts',
        element: (
          <RequirePalmettoFinanceUser>
            <ContractTemplates />
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/payouts/*',
        element: (
          <RequirePermissions
            permissions={[
              UserPermissions.admin,
              UserPermissions.orgAccountingAdmin,
              UserPermissions.lightReachAccountingReader,
              UserPermissions.lightReachAccountingSettings,
            ]}
            checkAllPermissions={false}
          >
            <Payouts />
          </RequirePermissions>
        ),
      },
      {
        path: '/admin/pricing/:programType/*',
        element: (
          <RequirePalmettoFinanceUser>
            <Pricing />
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/pricing/:programType/mappings/create',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
              <CreateMapping />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/pricing/:programType/price-caps/:priceCapType/schedules/create',
        element: (
          <FlagGuard required={['energyBackupCapSchedules']} redirectTo="/admin/pricing/energyBackup/price-caps">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
                <CreateCapSchedule />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/pricing/:programType/mappings/edit/:id',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
              <EditMapping />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/pricing/:programType/mappings/bulk-edit',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
              <BulkEditMappings />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/pricing/:programType/mappings/bulk-jobs',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
              <BulkJobs />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/pricing/:programType/price-sheet/:id',
        element: (
          <RequirePalmettoFinanceUser>
            <PriceSheet />
          </RequirePalmettoFinanceUser>
        ),
        children: [
          {
            index: true,
            element: <PriceSheetDetails />,
          },
          {
            path: '/admin/pricing/:programType/price-sheet/:id/mappings',
            element: <PriceSheetMappings />,
          },
        ],
      },
      {
        path: '/admin/pricing/:programType/price-sheet/edit/:id',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <EditPriceSheet />
          </RequirePermissions>
        ),
      },
      {
        path: '/admin/itc-eligibility-calculator',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <RequirePalmettoUserHasProgramTypes
              requiredProgramTypes={ItcEligibilityProgramTypes}
              checkAllProgramTypes={false}
            >
              <ITCCalculator />
            </RequirePalmettoUserHasProgramTypes>
          </RequirePermissions>
        ),
      },
      {
        path: '/admin/lead-management/*',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin]}
            checkAllPermissions={false}
          >
            <LeadManagement />
          </RequirePermissions>
        ),
      },
      {
        path: '/admin/sales-reps',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.admin]} checkAllPermissions={false}>
              <SalesReps />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
        children: [
          { index: true, element: <SalesRepsListView /> },
          { path: 'history', element: <SalesRepHistoryView /> },
        ],
      },
      {
        path: '/admin/sales-reps/:id',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.admin]} checkAllPermissions={false}>
              <SalesRepDetails />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/tasks/:taskType?',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachTaskAdmin]} checkAllPermissions={false}>
              <AdminTasks />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/quality-control/:setting?/*',
        element: (
          <RequirePalmettoFinanceUser>
            <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
              <QualityControlSettings />
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/checklists',
        element: (
          <FlagGuard required={['enableQcChecklists']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
                <ChecklistTemplates />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/checklists/new',
        element: (
          <FlagGuard required={['enableQcChecklists']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
                <ChecklistTemplateForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/checklists/:id/edit-draft',
        element: (
          <FlagGuard required={['enableQcChecklists']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
                <ChecklistTemplateForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/checklists/:id',
        element: (
          <FlagGuard required={['enableQcChecklists']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
                <ChecklistTemplateDetail />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/checklists/system-loss-thresholds',
        element: (
          <FlagGuard required={['enableQcChecklists']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.lightReachQCSettingsAdmin]}>
                <ChecklistAdminLayout>
                  <SystemLossThresholds />
                </ChecklistAdminLayout>
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        // Viewing is open to any sabal (internal) user; editing is restricted below.
        path: '/admin/approved-vendors',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <ApprovedVendorList />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/new',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
                <ApprovedVendorItemForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        // Viewing is open to any sabal (internal) user; editing is restricted below.
        path: '/admin/approved-vendors/:id',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <ApprovedVendorItemDetail />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/:id/edit',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
                <ApprovedVendorItemForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        // Viewing is open to any sabal (internal) user; editing is restricted below.
        path: '/admin/approved-vendors/manufacturers',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <EquipmentManufacturerList />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/manufacturers/new',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
                <EquipmentManufacturerForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        // Viewing is open to any sabal (internal) user; editing is restricted below.
        path: '/admin/approved-vendors/manufacturers/:id',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <EquipmentManufacturerDetail />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/manufacturers/:id/edit',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
                <EquipmentManufacturerForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        // Viewing is open to any sabal (internal) user; writes are gated below and in the page.
        path: '/admin/approved-vendors/product-lines',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <ProductLineList />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/product-lines/new',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.canImportAvlData]}>
                <ProductLineForm />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/approved-vendors/product-lines/:id',
        element: (
          <FlagGuard required={['enableAvlAdmin']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <ProductLineDetail />
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/hardware-exceptions',
        element: (
          <RequirePalmettoFinanceUser>
            <HardwareExceptionsTable />
          </RequirePalmettoFinanceUser>
        ),
      },
      {
        path: '/admin/promos',
        element: (
          <FlagGuard required={['accountPromos']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.pricingAdmin]}>
                <PromosTable />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/welcome-call-stip-rules',
        element: (
          <FlagGuard required={['welcomeCallStipRulesEnabled']} redirectTo="/admin">
            <RequirePalmettoFinanceUser>
              <RequirePermissions permissions={[UserPermissions.underwritingAdmin]}>
                <WelcomeCallStipRules />
              </RequirePermissions>
            </RequirePalmettoFinanceUser>
          </FlagGuard>
        ),
      },
      {
        path: '/admin/dashboard',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin]}
            checkAllPermissions={false}
          >
            <AgingDashboard />
          </RequirePermissions>
        ),
      },
      {
        path: '/account/create',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <CreateAccount />
          </RequirePermissions>
        ),
      },
      {
        path: '/account/application/success',
        element: (
          <RequirePermissions permissions={[]} allowInviteToken>
            <ApplicationSuccess />
          </RequirePermissions>
        ),
      },
      {
        path: '/accounts',
        element: <Accounts />,
      },
      {
        path: '/accounts/:id/edit',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <EditAccount />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/application/create',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
            allowInviteToken
          >
            <ReplayPrivacyMask>
              <ApplicationForm />
            </ReplayPrivacyMask>
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/disclosures',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
            allowInviteToken
          >
            <ReplayPrivacyMask>
              <DisclosureForm />
            </ReplayPrivacyMask>
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/disclosure-confirmation',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
            allowInviteToken
          >
            <ReplayPrivacyMask>
              <DisclosureConfirmation />
            </ReplayPrivacyMask>
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/quote/create',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <CreateQuote />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/change-order/create',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <FlagGuard required={['easyChangeOrder']} redirectTo="/">
              <CreateChangeOrder />
            </FlagGuard>
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/proof-of-pto/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <FlagGuard required={['ptoGreenlight']} redirectTo="/">
              <AdvancePtoForm />
            </FlagGuard>
          </RequirePermissions>
        ),
      },
      {
        //actions are create, edit, and review
        path: 'accounts/:id/loop-field-package/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <LoopFieldMilestonePackageFactory />
          </RequirePermissions>
        ),
      },
      {
        // actions are create and review
        path: 'accounts/:id/loop-field-design-documents/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <LoopFieldDesignDocSubmission />
          </RequirePermissions>
        ),
      },
      {
        //actions are create, edit, and review
        path: 'accounts/:id/installation-package/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <InstallMilestonePackage />
          </RequirePermissions>
        ),
      },
      {
        //actions are create, edit, and review
        path: 'accounts/:id/activation-package/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <ActivationMilestonePackage />
          </RequirePermissions>
        ),
      },
      {
        //actions are create, edit, and review
        path: 'accounts/:id/post-activation-package/:action',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <PostActivationMilestonePackage />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/:milestonePackage/flags/:flagId/:from?/:action?',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <FlagDetailsPage />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/production-value-check',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <ProductionValueCheck />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/itc-system-design',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <ITCSystemDesign />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/sales-rep-verification',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
            allowInviteToken
          >
            <SalesRepAuthentication />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/sales-rep-verification/authenticated',
        element: (
          <RequirePermissions permissions={[]} allowInviteToken>
            <VerificationSuccess />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/sales-rep-verification/error',
        element: (
          <RequirePermissions permissions={[]} allowInviteToken>
            <VerificationError />
          </RequirePermissions>
        ),
      },
      {
        path: 'accounts/:id/verify-existing-system',
        element: (
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.editor]}
            checkAllPermissions={false}
          >
            <VerifyExistingSystem />
          </RequirePermissions>
        ),
      },
      {
        path: '/accounts/:id/*',
        element: <Account />,
      },
      /**
       * @deprecated This will be replaced by the /queue route
       */
      {
        path: '/queues/*',
        element: <Queues />,
      },
      {
        path: '/queue/:type',
        element: <Queue />,
      },
      {
        path: '/queue/hold-motion/program-enablement',
        element: (
          <RequirePermissions permissions={[UserPermissions.lightReachQCSuperAdmin]}>
            <ProgramEnablement />
          </RequirePermissions>
        ),
      },
      {
        path: '/profile/*',
        element: <YourProfile />,
      },
      {
        path: '/profile/edit',
        element: <EditProfile />,
      },
      {
        path: '/settings/*',
        element: <Settings />,
      },
      // The accountctl API only mounts when SERVICE_ENV is local or next, so
      // outside those environments the route is left unregistered rather than
      // rendering a UI whose every request 404s.
      ...(isNextEnvironment()
        ? [
            {
              path: '/internal/accountctl/*',
              element: (
                <RequirePalmettoFinanceUser>
                  <AccountctlApp />
                </RequirePalmettoFinanceUser>
              ),
            },
            {
              path: '/internal/conga-test/*',
              element: (
                <RequirePalmettoFinanceUser>
                  <CongaTestApp />
                </RequirePalmettoFinanceUser>
              ),
            },
          ]
        : []),
    ],
  },
]);

(async (): Promise<void> => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: launchDarklyClientId,
    timeout: 10,
    user: {
      anonymous: true,
      key: anonymousUserKey,
    },
  });

  root.render(
    <LDProvider>
      <Provider store={store}>
        <AuthProvider>
          <ResponsiveProvider>
            <ThemeProvider storageKey="lightreach-ui-theme">
              <DsmModeSync>
                <IntercomProvider appId={intercomAppId}>
                  <RouterProvider router={router} />
                </IntercomProvider>
              </DsmModeSync>
            </ThemeProvider>
          </ResponsiveProvider>
        </AuthProvider>
      </Provider>
    </LDProvider>,
  );
})();
